/**
 * Haldiram theme enhancements: search UX, menu links, buy now, cart progress, cart drawer API.
 */
(function () {
  const listingLocks = new Set();

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '£{{amount}}');
    }
    return '£' + (cents / 100).toFixed(2);
  }

  function updateCartOffersProgress(cart) {
    if (!cart) return;
    document.querySelectorAll('.cart-shipping-progress').forEach((el) => {
      const tier1 = parseInt(el.dataset.tier1, 10) || 5000;
      const tier2 = parseInt(el.dataset.tier2, 10) || 7500;
      const tier3 = parseInt(el.dataset.tier3, 10) || 10000;
      const total = (cart && cart.total_price != null)
        ? cart.total_price
        : (parseInt(el.dataset.cartTotal, 10) || 0);
      let target = tier1;
      let label = 'Free delivery';
      let hintSuffix = 'for FREE delivery';
      let remaining = 0;
      let hintText = '';

      if (total >= tier3) {
        hintText = "You've unlocked 10% off and free delivery!";
      } else if (total >= tier2) {
        target = tier3;
        label = '10% off + free delivery';
        remaining = tier3 - total;
        hintSuffix = 'for 10% off + FREE delivery';
      } else if (total >= tier1) {
        target = tier2;
        label = '5% off + free delivery';
        remaining = tier2 - total;
        hintSuffix = 'for 5% off + FREE delivery';
      } else {
        remaining = tier1 - total;
      }

      const progressPct = Math.min(100, Math.round((total / tier3) * 100));

      const fill = el.querySelector('[data-progress-fill]');
      if (fill) fill.style.width = progressPct + '%';

      const icon = el.querySelector('.cart-shipping-progress__icon');
      if (icon) icon.classList.toggle('is-unlocked', total >= tier1);

      const title = el.querySelector('[data-progress-title]');
      if (title) title.textContent = label;

      const amount = el.querySelector('[data-progress-amount]');
      if (amount) {
        amount.textContent =
          total >= tier3 ? formatMoney(total) : formatMoney(total) + ' / ' + formatMoney(target);
      }

      const msg = el.querySelector('[data-offers-message]');
      if (msg) {
        msg.textContent =
          total >= tier3
            ? hintText
            : 'Add ' + formatMoney(Math.max(0, remaining)) + ' ' + hintSuffix;
      }
    });
  }

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function getDrawerSectionIds() {
    const drawer = getCartDrawer();
    if (!drawer || typeof drawer.getSectionsToRender !== 'function') {
      return 'cart-icon-bubble';
    }
    return drawer.getSectionsToRender().map((s) => s.id).join(',');
  }

  function getDrawerSectionsParam() {
    const drawerItems = document.querySelector('cart-drawer-items');
    if (drawerItems && typeof drawerItems.getSectionsToRender === 'function') {
      return drawerItems.getSectionsToRender().map((s) => s.section).join(',');
    }
    return getDrawerSectionIds();
  }

  function updateBubbleCount(count) {
    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
      if (count > 0) {
        bubble.style.display = '';
        const hiddenSpan = bubble.querySelector('[aria-hidden="true"]');
        if (hiddenSpan) hiddenSpan.textContent = count;
      } else {
        bubble.style.display = 'none';
      }
    });
  }

  function applyDrawerSections(response) {
    const drawer = getCartDrawer();
    if (!drawer || !response?.sections || typeof drawer.renderContents !== 'function') {
      return false;
    }
    drawer.classList.remove('is-empty');
    drawer.renderContents(response);
    return true;
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  function afterCartChange(cart) {
    if (!cart) return;
    updateCartOffersProgress(cart);
    updateBubbleCount(cart.item_count || 0);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'haldiram-cart' });
    }
    if (typeof window.syncListingCartFormsFromCart === 'function') {
      window.syncListingCartFormsFromCart(cart);
    }
  }

  async function addItem(variantId, quantity = 1) {
    const id = parseInt(variantId, 10);
    const lockKey = 'add-' + id;
    if (listingLocks.has(lockKey)) {
      return null;
    }
    listingLocks.add(lockKey);

    try {
      const res = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id,
          quantity,
          sections: getDrawerSectionsParam(),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        throw new Error(data.description || 'Could not add to cart');
      }

      applyDrawerSections(data);
      const cart = await fetchCart();
      afterCartChange(cart);
      return data;
    } finally {
      listingLocks.delete(lockKey);
    }
  }

  async function setVariantQuantity(variantId, quantity) {
    const targetVariantId = parseInt(variantId, 10);
    const qty = parseInt(quantity, 10);
    if (!targetVariantId || Number.isNaN(qty)) {
      throw new Error('Invalid cart update');
    }

    const lockKey = 'change-' + targetVariantId;
    if (listingLocks.has(lockKey)) {
      return null;
    }
    listingLocks.add(lockKey);

    try {
      const cart = await fetchCart();
      const lineIndex = cart.items.findIndex(
        (item) => String(item.variant_id) === String(targetVariantId)
      );

      if (lineIndex === -1) {
        if (qty <= 0) return null;
        throw new Error('Item not in cart');
      }

      const res = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: lineIndex + 1,
          quantity: qty,
          sections: getDrawerSectionsParam(),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status || data.errors) {
        throw new Error(data.description || data.message || 'Could not update cart');
      }

      applyDrawerSections(data);
      const updatedCart = await fetchCart();
      afterCartChange(updatedCart);
      return data;
    } finally {
      listingLocks.delete(lockKey);
    }
  }

  function refreshCartDrawerAndOpen() {
    const drawer = getCartDrawer();
    if (!drawer || typeof drawer.getSectionsToRender !== 'function') return Promise.resolve();

    const sectionIds = drawer.getSectionsToRender().map((s) => s.id).join(',');

    return fetch(`${window.location.pathname}?sections=${sectionIds}`)
      .then((r) => r.json())
      .then((sections) => {
        drawer.classList.remove('is-empty');
        drawer.renderContents({ sections });
        return fetchCart();
      })
      .then((cart) => {
        afterCartChange(cart);
      })
      .catch(() => {});
  }

  function initHeaderSearch() {
    const searchInput = document.querySelector('.header .search-input-hr');
    const predictive = document.querySelector('.header predictive-search');
    if (!searchInput || !predictive) return;

    searchInput.addEventListener('input', () => {
      if (searchInput.value.trim().length >= 1) {
        predictive.setAttribute('open', '');
      }
    });
  }

  function initMenuParentLinks() {
    document.querySelectorAll('.menu-hover-mega').forEach((mega) => {
      const link = mega.querySelector('.header__menu-item-link');
      if (!link) return;
      link.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        window.location.href = link.href;
      });
    });
  }

  function initBuyNow() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.instantBuy, .buynow-btn');
      if (!btn || btn.disabled) return;
      e.preventDefault();

      const variantId = btn.getAttribute('data-id') || btn.dataset.id;
      const qtyInput = document.querySelector('quantity-input .quantity__input');
      const quantity = parseInt(qtyInput?.value, 10) || 1;

      btn.disabled = true;
      const originalText = btn.querySelector('span')?.textContent;
      const span = btn.querySelector('span');
      if (span) span.textContent = 'Processing…';

      try {
        const res = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity }] }),
        });
        if (!res.ok) throw new Error('Add failed');
        window.location.href = '/checkout';
      } catch (err) {
        if (span && originalText) span.textContent = originalText;
        btn.disabled = false;
        alert('Unable to proceed to checkout. Please try again.');
      }
    });
  }

  function initHomepageBannerLinks() {
    document.querySelectorAll('.homepage-slide .slide-container-slide a').forEach((a) => {
      const href = a.getAttribute('href');
      if (!href || href === '#' || href.indexOf('javascript:void') === 0) {
        a.classList.add('is-disabled');
        a.removeAttribute('href');
        a.setAttribute('role', 'presentation');
      }
    });
  }

  window.updateCartOffersProgress = updateCartOffersProgress;

  window.HaldiramCart = {
    addItem,
    setVariantQuantity,
    refreshAndOpen: refreshCartDrawerAndOpen,
    fetchCart,
  };

  document.addEventListener('DOMContentLoaded', () => {
    initHeaderSearch();
    initMenuParentLinks();
    initBuyNow();
    initHomepageBannerLinks();

    fetchCart()
      .then(updateCartOffersProgress)
      .catch(() => {});
  });

  document.addEventListener('cart:updated', (e) => {
    if (!e.detail) return;
    updateCartOffersProgress(e.detail);
    if (typeof window.syncListingCartFormsFromCart === 'function') {
      window.syncListingCartFormsFromCart(e.detail);
    }
  });
})();

// Icon Marquee
document.querySelectorAll('.icon-with-text-marquee').forEach((marquee) => {
  const track = marquee.querySelector('.icon-with-text-marquee__track');

  while (track.scrollWidth < marquee.offsetWidth * 2) {
    track.appendChild(track.children[0].cloneNode(true));
  }
});